/* date.ts */
import Languages from '@shared/types/enums/languages';
import TranslationManager from '../assets/locale/translationsManager';

const DateUtils = {
  formatTime(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  },
  getCurrentDate() {
    const currentDate = new Date();
    return (
      currentDate.getFullYear() +
      '/' +
      String(currentDate.getMonth() + 1) +
      '/' +
      String(currentDate.getDate())
    );
  },
  getChunkFromDate(
    offsetHours: number,
    date: Date = new Date()
  ): { start: Date; end: Date } {
    const chunkStartHour =
      Math.floor(date.getHours() / offsetHours) * offsetHours;
    const start = new Date(date);
    start.setHours(chunkStartHour, 0, 0, 0);
    const end = new Date(start);
    end.setHours(chunkStartHour + offsetHours, 0, 0, 0);
    return { start, end };
  },
  getDateFromOffset(offset: number) {
    const date = new Date();
    date.setDate(date.getDate() + offset);
    return (
      date.getFullYear() +
      '/' +
      String(date.getMonth() + 1) +
      '/' +
      String(date.getDate())
    );
  },
  // Returns a translated day label: "Today", "Yesterday", "Tomorrow", or "<day name> <day number>" for all other dates.
  getDayTitle(date: Date, trimDayName = false): string {
    const today = new Date();
    const isSameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    if (isSameDay(date, today)) {
      return TranslationManager.get('MISC.DATE.TODAY');
    }

    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (isSameDay(date, yesterday)) {
      return TranslationManager.get('MISC.DATE.YESTERDAY');
    }

    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    if (isSameDay(date, tomorrow)) {
      return TranslationManager.get('MISC.DATE.TOMORROW');
    }

    let dayLiteral = TranslationManager.get(
      'MISC.DATE.DAYS.INDEX.' + date.getDay()
    );
    if (trimDayName) dayLiteral = dayLiteral.substring(0, 3);
    return `${dayLiteral} ${date.getDate()}`;
  },
  getDurationInMinutes: (start?: number, end?: number) => {
    if (!start || !end) return 0;
    let duration = end - start;
    if (duration) {
      duration = duration / 1000 / 60;
      return Math.round(duration);
    }
    return 0;
  },
  getDurationInHours: (start?: number, end?: number) => {
    if (!start || !end) return 0;
    let duration = end - start;
    if (duration) {
      duration = duration / 1000 / 60 / 60;
      return duration;
    }
    return 0;
  },
  getOffset: (start: number, end: number) => {
    const totalMs = Math.max(0, end - start);
    const milliseconds = totalMs % 1000;

    let n = Math.floor(totalMs / 1000);
    const seconds = n % 60;
    n = Math.floor(n / 60);
    const minutes = n % 60;
    n = Math.floor(n / 60);
    const hours = n % 24;
    const days = Math.floor(n / 24);

    return { days, hours, minutes, seconds, milliseconds };
  },
  getOrdinalNumber: (number: number) => {
    if (TranslationManager.currentLanguage === Languages.ENGLISH) {
      const suffixes = ['th', 'st', 'nd', 'rd'];
      const remainder = number % 100;
      return (
        number +
        (suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0])
      );
    }
    return number.toString();
  },
  getDayOfTheWeek(timeStamp?: number) {
    const date =
      timeStamp !== undefined && timeStamp >= 0
        ? new Date(timeStamp)
        : new Date();
    const dayOfWeek = date.getDay();
    if (dayOfWeek > -1) {
      return TranslationManager.get('MISC.DATE.DAYS.INDEX.' + dayOfWeek);
    }
    return null;
  },
  getTime({
    customDate,
    displaySeconds = true,
    displayMinutes = true,
    displayHours = true,
    displayDayOfWeek = false,
    displayDay = false,
    displayToday = true,
    displayMonth = false,
    displayMonthPreposition = false,
    displayYear = false,
    ordinalDay = true,
    separator = ' · ',
    trimDayOfTheWeek = false,
    trimMonth = false,
    utc = false,
  }: {
    customDate?: number | string;
    displaySeconds?: boolean;
    displayMinutes?: boolean;
    displayHours?: boolean;
    displayDay?: boolean;
    displayToday?: boolean;
    displayDayOfWeek?: boolean;
    displayMonth?: boolean;
    displayMonthPreposition?: boolean;
    displayYear?: boolean;
    ordinalDay?: boolean;
    separator?: string;
    trimDayOfTheWeek?: boolean;
    trimMonth?: boolean;
    utc?: boolean;
  } = {}) {
    try {
      const date =
        customDate !== undefined && customDate
          ? new Date(customDate)
          : new Date();
      const isEnglish =
        TranslationManager.currentLanguage === Languages.ENGLISH;
      let currentTime = '';

      if (displayDayOfWeek) {
        const currentDate = new Date();
        const isToday =
          displayToday &&
          date.getDate() === currentDate.getDate() &&
          date.getMonth() === currentDate.getMonth() &&
          date.getFullYear() === currentDate.getFullYear();
        const dayOfWeek = utc ? date.getUTCDay() : date.getDay();
        if (dayOfWeek > -1) {
          currentTime += isToday
            ? TranslationManager.get('MISC.DATE.TODAY')
            : TranslationManager.get('MISC.DATE.DAYS.INDEX.' + dayOfWeek);
          if (trimDayOfTheWeek) {
            currentTime = currentTime.substring(0, 3);
          }
          currentTime +=
            displayDay || displayMonth || displayYear
              ? separator
              : displaySeconds || displayMinutes || displayHours
              ? ' '
              : '';
        }
      }

      if (displayDay && !isEnglish) {
        const day = utc ? date.getUTCDate() : date.getDate();
        currentTime += day + ' ';
      }

      if (displayMonth) {
        const month = utc ? date.getUTCMonth() : date.getMonth();
        if (month > -1) {
          if (displayMonthPreposition && !isEnglish) {
            currentTime +=
              TranslationManager.get('MISC.DATE.MONTHS.PREPOSITION') + ' ';
          }
          let monthLiteral = TranslationManager.get(
            'MISC.DATE.MONTHS.INDEX.' + month
          );
          if (trimMonth) {
            monthLiteral = monthLiteral.slice(0, 3);
            if (displayDay) {
              monthLiteral += '.';
            }
          }
          currentTime += monthLiteral;
        }
      }

      if (displayDay && isEnglish) {
        const day = utc ? date.getUTCDate() : date.getDate();
        currentTime +=
          ' ' + (ordinalDay ? DateUtils.getOrdinalNumber(day) : day);
      }

      if (displayMonth || (displayDay && isEnglish)) {
        const nextSeparator =
          (displayYear && !displayHours) ||
          (!displayYear && (displayHours || displayMinutes || displaySeconds))
            ? separator
            : displayYear
            ? ' '
            : '';
        const monthEndsWithPeriod = trimMonth && displayDay;
        currentTime +=
          monthEndsWithPeriod && nextSeparator.startsWith('.')
            ? nextSeparator.slice(1)
            : nextSeparator;
      }

      if (displayYear) {
        const year = utc ? date.getUTCFullYear() : date.getFullYear();
        currentTime += year;
        currentTime +=
          displayHours || displayMinutes || displaySeconds ? separator : '';
      }

      if (displayHours) {
        const hours = utc ? date.getUTCHours() : date.getHours();
        currentTime += hours > 9 ? hours : `0${hours}`;
      }
      if (displayMinutes) {
        const minutes = utc ? date.getUTCMinutes() : date.getMinutes();
        currentTime += ':' + (minutes > 9 ? minutes : `0${minutes}`);
      }
      if (displaySeconds) {
        const seconds = utc ? date.getUTCSeconds() : date.getSeconds();
        currentTime += ':' + (seconds > 9 ? seconds : `0${seconds}`);
      }
      return currentTime;
    } catch (e) {
      console.error(e);
      return '';
    }
  },
  getYear(date: number) {
    const currentDate = new Date(date);
    return currentDate.getFullYear();
  },
  isCurrentDate(start?: number, end?: number) {
    if (!start || !end) return false;
    const current = Date.now();
    return start <= current && end >= current;
  },
  isPastDate(date?: number) {
    if (!date) return false;
    const current = Date.now();
    return date < current;
  },
  isFutureDate(date?: number) {
    if (!date) return false;
    const current = Date.now();
    return date > current;
  },
  isSameDate(initDate: Date, endDate: Date) {
    return (
      initDate.getFullYear() === endDate.getFullYear() &&
      initDate.getMonth() === endDate.getMonth() &&
      initDate.getDate() === endDate.getDate()
    );
  },
};

export default DateUtils;
